import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import Button from './Button';
import { Checkbox } from '@components/common';
import type { Role } from '@app-types';
import styles from './RoleFilterDropdown.module.css';

interface RoleFilterDropdownProps {
  roles: Role[];
  selectedRoleIds: number[];
  onSelectionChange: (roleIds: number[]) => void;
  roleCounts: { id: number; name: string; count: number }[];
  dataTestId?: string;
}

const RoleFilterDropdown: React.FC<RoleFilterDropdownProps> = ({
  roles,
  selectedRoleIds,
  onSelectionChange,
  roleCounts,
  dataTestId = 'role-filter'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleRole = (roleId: number) => {
    if (selectedRoleIds.includes(roleId)) {
      onSelectionChange(selectedRoleIds.filter((id) => id !== roleId));
    } else {
      onSelectionChange([...selectedRoleIds, roleId]);
    }
  };

  const clearSelection = () => {
    onSelectionChange([]);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getCountForRole = (roleId: number): number => {
    const roleCount = roleCounts.find((r) => r.id === roleId);
    return roleCount?.count || 0;
  };

  return (
    <div className={styles.roleFilterDropdown} ref={dropdownRef} data-test-id={`${dataTestId}-dropdown`}>
      <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)} dataTestId={`${dataTestId}-toggle-button`}>
        Filter by Role
        {selectedRoleIds.length > 0 && ` (${selectedRoleIds.length})`}
        <ChevronDown size={16} className={styles.icon} />
      </Button>

      {isOpen && (
        <div className={styles.roleFilterDropdownMenu} data-test-id={`${dataTestId}-menu`}>
          <div className={styles.roleFilterDropdownHeader}>
            <span>Select Roles</span>
            {selectedRoleIds.length > 0 && (
              <button onClick={clearSelection} className={styles.roleFilterDropdownClear}>
                Clear
              </button>
            )}
          </div>
          <div className={styles.roleFilterDropdownContent}>
            {roles.map((role) => (
              <div key={role.id} className={styles.roleFilterDropdownOption}>
                <Checkbox
                  checked={selectedRoleIds.includes(role.id)}
                  onChange={() => toggleRole(role.id)}
                  label={role.name}
                  wrapperClassName={styles.roleFilterDropdownCheckboxWrapper}
                />
                <span className={styles.roleFilterDropdownCount}>{getCountForRole(role.id)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleFilterDropdown;
